<template>
    <div class="wizard-forms wizard-forms__confirmation">
        <br />
        <div v-if="withToggle" class="row">
            <div class="col-md-10 col-md-offset-1">
                <div class="vrm-info-box--flex">
                    <span>
                        {{
                            $t(`dynamicess.toggle`, {
                                i18nFallback:
                                    "Toggle Dynamic ESS for this installation. By turning this on, you will be able to set Dynamic ESS to Auto, Buy, Sell or Off from the Controls menu on your installation's dashboard.",
                            })
                        }}
                    </span>
                    <vrm-controlled-toggle
                        :disabled="!userCanEdit"
                        :value="toggle"
                        @input="$emit('toggle', !toggle)"
                    />
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-10 col-md-offset-1">
                <h4 v-if="!withToggle">
                    {{ $t('dynamicess.wizard.confirmation', { i18nFallback: 'Confirmation' }) }}
                </h4>
                <p
                    v-if="!withToggle"
                    v-html="
                        $t('dynamicess.confirmationDescription', {
                            i18nFallback: translationsFallbacks.confirmationDescription,
                        })
                    "
                ></p>
                <div class="sg-spread">
                    <h4>{{ $t('dynamicess.wizard.grid', { i18nFallback: 'System' }) }}</h4>
                    <a v-if="userCanEdit" href="#" @click.prevent="$emit('edit', STEPS.GRID)">
                        {{ $t('dynamicess.edit', { i18nFallback: 'Edit' }) }}
                    </a>
                </div>
                <table class="vrm-table vrm-table--fixed vrm-table--compact vrm-table--borderless">
                    <tbody>
                        <tr v-for="({ label, value }, idx) in form.grid" :key="idx">
                            <td v-html="label"></td>
                            <td v-html="value"></td>
                        </tr>
                    </tbody>
                </table>
                <div class="sg-spread">
                    <h4>{{ $t('dynamicess.wizard.battery', { i18nFallback: 'Battery' }) }}</h4>
                    <a v-if="userCanEdit" href="#" @click.prevent="$emit('edit', STEPS.BATTERY)">
                        {{ $t('dynamicess.edit', { i18nFallback: 'Edit' }) }}
                    </a>
                </div>
                <table class="vrm-table vrm-table--fixed vrm-table--compact vrm-table--borderless">
                    <tbody>
                        <tr v-for="({ label, value, table }, idx) in form.battery" :key="idx">
                            <td v-if="!table" v-html="label"></td>
                            <td v-if="!table" v-html="value"></td>
                            <schedule-confirmation-table
                                v-if="table"
                                :type-label="$t(`ess.restrict_${restrictionType}`)"
                                :rows="value"
                                :value-formatter="formatRestriction"
                                value-field="restrict"
                            />
                        </tr>
                    </tbody>
                </table>
                <div class="sg-spread">
                    <h4>{{ $t('dynamicess.wizard.buyPrices', { i18nFallback: 'Buy prices' }) }}</h4>
                    <a
                        v-if="userCanEdit"
                        href="#"
                        @click.prevent="$emit('edit', STEPS.ENERGY_PRICES_BUY)"
                    >
                        {{ $t('dynamicess.edit', { i18nFallback: 'Edit' }) }}
                    </a>
                </div>
                <table class="vrm-table vrm-table--fixed vrm-table--compact vrm-table--borderless">
                    <tbody>
                        <tr v-for="({ label, value, table }, idx) in form.buy" :key="idx">
                            <td v-if="!table" v-html="label"></td>
                            <td v-if="!table" v-html="value"></td>
                            <schedule-confirmation-table
                                v-if="table"
                                :type-label="$t('ess.price', { i18nFallback: 'Price' })"
                                :rows="value"
                                :value-formatter="formatPrice"
                                value-field="price"
                            />
                        </tr>
                    </tbody>
                </table>
                <div v-if="energyGridSell" class="sg-spread">
                    <h4>
                        {{ $t('dynamicess.wizard.sellPrices', { i18nFallback: 'Sell prices' }) }}
                    </h4>
                    <a
                        v-if="userCanEdit"
                        href="#"
                        @click.prevent="$emit('edit', STEPS.ENERGY_PRICES_SELL)"
                    >
                        {{ $t('dynamicess.edit', { i18nFallback: 'Edit' }) }}
                    </a>
                </div>
                <div v-if="energyGridSell">
                    <table
                        class="vrm-table vrm-table--fixed vrm-table--compact vrm-table--borderless"
                    >
                        <tbody>
                            <tr v-for="({ label, value, table }, idx) in form.sell" :key="idx">
                                <td v-if="!table" v-html="label"></td>
                                <td v-if="!table" v-html="value"></td>
                                <schedule-confirmation-table
                                    v-if="table"
                                    :type-label="$t('ess.price', { i18nFallback: 'Price' })"
                                    :rows="value"
                                    :value-formatter="formatPrice"
                                    value-field="price"
                                />
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <!--
        <div class="row">
            <div class="col-md-10 col-md-offset-1">
                <potential-earnings v-if="!withToggle || !toggle" />
            </div>
        </div>
        -->
    </div>
</template>
<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { DYNAMIC_ESS } from '@/vue/store/modules/dynamic-ess-form.js';
import { STEPS } from '@/vue/components/pages/dynamic-ess/constants.js';
import ScheduleConfirmationTable from '@/vue/components/pages/dynamic-ess/forms/ScheduleConfirmationTable.vue';
import VrmControlledToggle from '@/vue/components/shared/toggles/VrmControlledToggle';
import { CURRENCIES } from '@/vue/store/modules/currencies';
import PotentialEarnings from '@/vue/components/shared/dynamic-ess/PotentialEarnings.vue';

@Component({
    components: {
        ScheduleConfirmationTable,
        VrmControlledToggle,
        PotentialEarnings,
    },
})
export default class Confirmation extends Vue {
    @Prop({ type: Boolean, default: false })
    withToggle;
    @Prop()
    toggle;
    @Prop()
    userCanEdit;

    translationsFallbacks = {
        confirmationDescription:
            "Check and edit your configuration before saving it. If you want to know more about Dynamic ESS, please visit our <a href='https://www.victronenergy.com/live/drafts:dynamic_ess' target='_blank'>documentation page</a>.",
    };

    STEPS = STEPS;
    get energyGridSell() {
        return this.$store.state.dynamicEssForm.energyGridSell;
    }

    get form() {
        return this.$store.getters[DYNAMIC_ESS.FORM](this.$store.getters[CURRENCIES.GET_ACTIVE]);
    }

    formatPrice(price) {
        const value = parseFloat(price).toFixed(
            Math.max(2, (price.toString().split('.')[1] || []).length),
        );
        return this.currency.sign + value;
    }

    formatRestriction(restriction) {
        return restriction ? this.$t('common.yes') : this.$t('common.no');
    }

    get currency() {
        return this.$store.getters[CURRENCIES.GET_ACTIVE];
    }

    get restrictionType() {
        return this.$store.getters[DYNAMIC_ESS.BATTERY_FLOW_RESTRICTION_TYPE];
    }
}
</script>
